import React from 'react';
import ReactGA from 'react-ga4';
import { formatPrice, formatUfPrice } from '../helpers/utils';
import { IPlan } from '../store/Quote';
import PlanTooltip from './PlanTooltip';

interface IPlanProperties {
  benefits: string;
  campaign?: string;
  characteristics?: string;
  deductible: number;
  details: string;
  id: string;
  name: string;
}

interface IPlanPremiums {
  annual: number;
  local: number;
  monthly: number;
  period: string;
  total: number;
}

interface IPlanCompany {
  id: string;
  image: string;
  name: string;
}

interface IPlanProps {
  charges: string;
  company: IPlanCompany;
  premiums: IPlanPremiums;
  properties: IPlanProperties;
  selectPlan: (payload: IPlan) => any;
  beneficiaryCap: number;
  beneficsPdf: string;
  consultation: boolean;
  deductible: string;
  eventCap: number;
  refund: number;
  planNumber: number;
  toggle: any;
  togglePdf: any;
  toggleTope: any;
  toggleCoverage: any;
  toggleConsultation: any;
  [key: string]: any;
  toggleAmbulatory: any;
  toggleMaternity: any;
}

export default function Plan(props: IPlanProps) {
  const {
    charges,
    company: { id: companyId, name: companyName },
    selectPlan,
    properties: { id, name: planName },
    premiums: { monthly, local, annual },
    consultation,
    beneficiaryCap,
    beneficsPdf,
    deductible,
    refund,
    toggle,
    togglePdf,
    toggleTope,
    toggleCoverage,
    toggleConsultation,
    planNumber,
    ambulatory,
    ambulatoryFull,
    hospitable,
    maternity,
    kinesiologyHealth,
    toggleAmbulatory,
    toggleAmbulatoryFull,
    toggleMaternity,
    toggleHospitable,
    toggleKinesiologyHealth,
  } = props;
  const select = () =>
    selectPlan({
      companyId,
      companyName,
      name: `Plan ${planNumber + 1} - Titular ${charges}`,
      planName,
      plan_id: id,
      premium_annual: annual,
      premium_monthly: monthly,
      premium_monthly_CLP: local,
    });

  const userInteraction = () => {
    ReactGA.event('user_interaction', {
      action: 'ver-beneificios-y-asistencias',
      location: 'paso-cotizacion',
      flow: 'salud-complementario',
    });
  };

  const toggleAll = () => (_: any) => {
    toggle(true)();
    togglePdf(beneficsPdf)();
    toggleCoverage(refund)();
    toggleTope(beneficiaryCap)();
    toggleConsultation(consultation)();
    userInteraction();
    toggleAmbulatory(ambulatory)();
    toggleAmbulatoryFull(ambulatoryFull)();
    toggleMaternity(maternity)();
    toggleHospitable(hospitable)();
    toggleKinesiologyHealth(kinesiologyHealth)();
  };

  return (
    <article className="col-xl-6 col-plan-slide">
      <div className="custom-plan-card">
        <header className="plan-card-header d-flex justify-content-center align-items-center align-content-center">
          Plan {planNumber + 1} -{' '}
          {ambulatory ? 'Ambulatorio' : ambulatoryFull ? 'Full' : 'Reembolso'}{' '}
          {refund}% <br />{' '}
          {ambulatory || ambulatoryFull
            ? '(Tope Anual ' + beneficiaryCap + 'UF)'
            : ''}
        </header>
        <section>
          <figure className="card-plan-logo w-100 d-flex justify-content-center align-items-center align-content-center">
            <img
              className="w-40"
              src="//sfestaticos.blob.core.windows.net/guideline/companies/metlife.svg"
              alt="Metlife"
            />
          </figure>
          <div className="card-plan-price w-100 d-flex flex-column justify-content-center align-items-center align-content-center">
            <h3>${formatPrice(local)}</h3>
            <p className="m-0">UF {formatUfPrice(monthly)} mensual</p>
          </div>
          <div className="card-plan-message w-100 d-flex flex-column justify-content-center align-items-center align-content-center">
            ¡No lo dudes, asegúrate hoy!
          </div>
          {ambulatory || ambulatoryFull ? (
            <div className="card-plan-tooltips">
              <PlanTooltip
                planIndex={planNumber}
                index={1}
                title="Gastos Ambulatorios"
                info={ambulatory || ambulatoryFull ? 'Si' : 'No'}
                text="Son los gastos médicos incurridos por el asegurado a causa del tratamiento de una incapacidad que no requiere de su hospitalización, esto aun cuando el tratamiento se haya efectuado en un hospital."
              />
              <PlanTooltip
                planIndex={planNumber}
                index={2}
                title="Gastos Hospitalarios"
                info={hospitable ? 'Si' : 'No'}
                text="Corresponde a consultas médicas intrahospitalarias, salas de urgencia; derecho de pabellón;  exámenes de laboratorio e imagenología, ultrasonografía y medicina nuclear, procedimientos especiales; equipos, materiales, insumos y medicamentos; otros gastos suministrados al asegurado durante su hospitalización y que hayan sido debidamente prescritos por el médico tratante durante la hospitalización como necesarios para el tratamiento de la incapacidad que la motivó."
              />
              <PlanTooltip
                planIndex={planNumber}
                index={3}
                title="Maternidad"
                info={maternity ? 'Si' : 'No'}
                text="Para que el asegurado titular o cónyuge asegurado tenga derecho a este beneficio, la fecha probable de inicio del embarazo debe ser posterior a la fecha de inicio de la vigencia de esta cobertura para el asegurado que se aplica."
              />
              <PlanTooltip
                planIndex={planNumber}
                index={4}
                title="Salud Mental"
                info={kinesiologyHealth ? 'Si' : 'No'}
                text="Corresponde a reembolso de consultas psicológicas/psiquiátricas."
              />
            </div>
          ) : (
            <div className="card-plan-tooltips">
              <PlanTooltip
                planIndex={planNumber}
                index={1}
                title="Consulta médica"
                info={consultation ? 'Si' : 'No'}
                text="Consulta a un profesional médico para diagnóstico, examen, tratamiento, seguimiento, consejo o cualquier otro trámite por algún problema, molestia o enfermedad."
              />
              <PlanTooltip
                planIndex={planNumber}
                index={2}
                title="Reembolso"
                info={refund + '%'}
                text="Devolución de una cantidad de dinero a la persona que había pagado o debería haber pagado."
              />
              <PlanTooltip
                planIndex={planNumber}
                index={3}
                title="Tope anual"
                info={'UF ' + beneficiaryCap}
                text="Monto máximo que tu seguro reembolsará al año."
              />
              <PlanTooltip
                planIndex={planNumber}
                index={4}
                title="Deducible"
                info={'UF ' + deductible}
                text="Monto que debes pagar para hacer uso y se active el reembolso del seguro, una vez pagado el deducible tu seguro te reembolsará hasta llegar al tope."
              />
            </div>
          )}
        </section>
        <section id='planPromo'>
        </section>
        <footer className="card-plan-footer">
          <button
            className="btn white btn--hover-primary matrix-button"
            type="button"
            onClick={select}
          >
            Quiero contratar
          </button>
          <span
            className="benefits-asistance-button d-flex justify-content-center align-items-center align-content-center px-14 cursor-pointer"
            onClick={toggleAll()}
          >
            Ver beneficios y asistencias
            <i className="material-icons">chevron_right</i>
          </span>
        </footer>
      </div>
    </article>
  );
}
